<template>
  <div ref="elementRef" v-style:bg="backgroundColor" :class="{ 'py-6': backgroundColor }">
    <div :class="{ 'container max-w-full lg:px-6': !isFullWidth }">
      <div v-style:m="containerMargin">
        <div
          v-if="title || subtitle || textLink"
          class="f-col md:flex-row md:items-end"
          :class="[
            isSimplifiedTitle ? 'pb-2' : 'pb-3',
            [alignment, titleAlignment, titlePosition].includes('center')
              ? 'items-center text-center md:justify-center '
              : 'md:between',
            { '<md:px-4': isStackedLayout },
          ]"
        >
          <div :class="{ [titlePadding]: isFullWidth && !isSimplifiedTitle }">
            <component
              :is="titleTag"
              v-if="title"
              v-style:c="titleColor"
              class="pb-2 pt-6 lg:pb-4"
              :class="[
                titleStyle,
                { [titleStyleClasses!]: !isSimplifiedTitle },
              ]"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </component>
            <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
              <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
              <span v-else>{{ subtitle }}</span>
            </p>
          </div>
          <cms-shared-button
            v-if="textLink"
            v-bind="{ ...textLink, ...getTextLinkStyles(content) }"
          />
        </div>

        <vf-carousel-elevate
          ref="carousel"
          v-bind="carouselSettings"
          :class="[collectionAlignment, carouselClasses, paddingClasses]"
          :class-container="classContainer"
          :class-controls="classControls"
          :class-controls-inner="classControlsInner"
          :style="content.type === 'VfCanvasCTAButtonCollection' ? { 'padding-block': '1px' } : {}"
          :style-container="{
            '--p': mobileSlidePadding,
            '--p-md': 0,
            ...(sm?.slidePadding ? { '--m': `${spacing[sm.slidePadding]}rem` } : {}),
            ...(md?.slidePadding ? { '--m-md': `${spacing[md.slidePadding]}rem` } : {}),
            ...(lg?.slidePadding ? { '--m-lg': `${spacing[lg.slidePadding]}rem` } : {}),
          }"
        >
          <template #default="{ paused }">
            <div
              v-for="(item, index) in items"
              :key="item.id"
              v-style="{
                'max-w': slideMaxWidth,
                'p': slidePadding,
                'w': getAdjustedSlideWidth(index),
              }"
              :data-test-id="`${item.type}-${item.id}`"
              :data-tracking-id="item.delocalizedTracking"
              :interval="
                item.media?.[$viewport.breakpoint]?.duration
                  ? item.media?.[$viewport.breakpoint]?.duration * 1000
                  : undefined
              "
            >
              <component
                :is="`lazy-cms-${resolveContentComponent(item, mappings)?.componentType}`"
                v-style="{
                  ...brandStyles[isFullHeight ? 'containerFullHeight' : 'container']?.(headerHeight, promoBarHeight),
                  h: isFullScreen && { sm: '50vh', md: '100vh' },
                }"
                class="h-full"
                :class="[itemClass, { 'children:h-inherit': isFullHeight }]"
                :content="{ ...item, isFullWidth, isFullHeight }"
                :controls="!carouselSettings.showProgress"
                :first-element
                :full-height="isFullScreen"
                :index
                is-part-of-collection
                :parent-heading-level="title && +titleTag.charAt(1)"
                :paused
              />
            </div>
          </template>
          <template #pagination="{ activeItem }">
            <div
              v-if="carouselSettings.showPagination && items.length"
              class="flex justify-center pt-6 space-x-2 !absolute !right-0 !top-0 !mr-5 !mt-5 !bg-brand-2/50 !p-1"
            >
              <base-button
                v-for="(_, i) in items"
                :key="i"
                :aria-current="activeItem === i"
                :aria-label="replaceAll($t.goToSlide, { slide: i + 1 })"
                class="h-2 w-2 b rounded-full !h-4 !w-4 !rounded-0"
                :class="activeItem === i ? 'b-grey-10 bg-grey-10' : 'b-grey-40 !b-grey-10 bg-grey-80'"
                @click="carousel.scrollTo(i)"
              />
            </div>
          </template>
        </vf-carousel-elevate>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { CollectionContent } from '#types/components/cms/collection'
import { ParentPromotionTrackingKey } from '#content/contexts/promotionTracking'
import mappings from '#content/mappings'

const { content, firstElement } = defineProps<{
  content: CollectionContent<any>
  firstElement?: boolean
}>()

const {
  brandStyles,
  classControls: defaultClassControls,
  classControlsInner,
  showArrowsOnHoverOnly,
  showArrowsOnHoverThreshold,
  sizeControls,
  spacing,
  titlePadding,
  [content.type]: { carouselClasses, itemClass, itemsProp, titleStyleClasses } = {}
} = useAppConfig().components.cms.collection
const { breakpoints } = useAppConfig().ds
const { promoBarHeight } = useAppConfig().components.vf.header
const header = useHeaderStore()
const { $viewport } = useNuxtApp()
const { width: windowWidth } = useWindowSize()

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)

const isFullScreen = computed(() => content.variant === 'twoCardGridFullHeight')

const carousel = ref()

const isButtonCollectionOverflowing = ref(false)

watchEffect(() => {
  if (carousel.value && content.type === 'VfCanvasCTAButtonCollection')
    isButtonCollectionOverflowing.value = carousel.value.carousel.containerWidth > windowWidth.value
})

const { lg, md, sm } = content.breakpoints

// ===================================
// Temporary Solution for stacked collection
// start
// ===================================
const isStackedLayout = computed(() => content?.name!.indexOf('[stacked]') >= 0)

if (isStackedLayout.value) {
  // eslint-disable-next-line vue/no-mutating-props
  content.isFullWidth = true
  if (lg) {
    lg.type = 'grid'
    lg.slideWidth = '100%'
  }

  if (md) {
    md.type = 'grid'
    md.slideWidth = '100%'
  }
}
// ===================================
// Temporary Solution for stacked collection
// end
// ===================================

const {
  alignment,
  backgroundColor,
  equalTargetSize,
  isFullHeight,
  isFullWidth,
  name,
  promotionTracking,
  subtitle,
  subtitleColor,
  subtitleHtml,
  subtitleStyle,
  textLink,
  title,
  titleAlignment,
  titleColor,
  titleHeadingTag,
  titleHtml,
  titlePosition,
  titleStyle,
  variant,
} = content

const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)
provide(ParentPromotionTrackingKey, {
  parentPromotionName: promotionTracking?.promotionName,
  parentHandlePromotionClick: handlePromotionClick
})

const items = computed(() => (itemsProp ? content[itemsProp] : content.items) || [])

const collectionAlignment = computed(() => {
  if (alignment === 'center') {
    return {
      'lg:justify-center lg:flex': lg?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '~md:justify-center ~md:flex': md?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '<md:justify-center <md:flex': sm?.type === 'carousel' && !isButtonCollectionOverflowing.value
    }
  }
  else if (alignment === 'right') {
    return {
      'lg:justify-end lg:flex': lg?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '~md:justify-end ~md:flex': md?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '<md:justify-end <md:flex': sm?.type === 'carousel' && !isButtonCollectionOverflowing.value
    }
  }
})

const getItemWidth = (w?: string) => {
  if (w === 'auto') return `${100 / items.value.length}%`
  return w || 'auto'
}

const getItemMaxWidth = (maxW?: string) => maxW || 'none'

const slideWidth: Record<keyof Responsive, string> = Object.keys(breakpoints).reduce((acc, cur) => {
  const width = getItemWidth(
    getValueForBreakpoint(cur as keyof Responsive, equalTargetSize)
    && content.type === 'VfCanvasCTAButtonCollection'
    && content.breakpoints[cur].type !== 'grid'
      ? `calc(12.5rem + ${2 * spacing[content.breakpoints[cur].slidePadding] || 0}rem)`
      : content.breakpoints[cur].slideWidth
  )

  return { ...acc, [cur]: width }
}, {} as Record<keyof Responsive, string>)

const getAdjustedSlideWidth = (index: number) => {
  const mapping = {
    threeCardGrid: !index && { sm: slideWidth.sm, md: '50%' },
    // more mappings to come
  }

  return mapping[variant] || slideWidth
}

const slideMaxWidth: Record<keyof Responsive, string> = {
  sm: getItemMaxWidth(sm?.slideMaxWidth),
  md: getItemMaxWidth(md?.slideMaxWidth),
  lg: getItemMaxWidth(lg?.slideMaxWidth)
}

// Mobile carousel centring
const mobileSlideWidth = Number.parseInt(slideWidth.sm)
const mobileSlidePadding = Number.isNaN(mobileSlideWidth) ? 0 : `0 0 0 ${(100 - mobileSlideWidth) / 2}%`

const containerMargin: Record<keyof Responsive, any> = {
  sm: sm?.margin && `0 ${sm.margin}`,
  md: md?.margin && `0 ${md.margin}`,
  lg: lg?.margin && `0 ${lg.margin}`
}

// Create padding for each slide
const slidePadding = Object.keys(breakpoints)
  .reduce((acc, bp) => ({
    ...acc,
    ...(content.breakpoints[bp]?.slidePadding && {
      [bp]: `${spacing[content.breakpoints[bp].slidePadding] || 0}rem`
    })
  }), {})

const classControls = ((): string[] => {
  const hiddenClass = {
    sm: '<md:hidden',
    md: '~md:hidden',
    lg: 'lg:hidden'
  }

  return Object.keys(hiddenClass).reduce((acc, bp) => {
    if (content.breakpoints[bp]?.type === 'grid') return [...acc, hiddenClass[bp]]
    // show arrows by default if showArrows is not set
    return [...acc, ...(content.breakpoints[bp]?.showArrows ?? true) ? [] : [hiddenClass[bp]]]
  }, [defaultClassControls])
})()

const isItemTextLink = (bp: keyof Responsive, index: number) => getValueForBreakpoint(bp, items.value.at(index)?.target?.style) === 'text-link'

const paddingClasses = content.type === 'VfCanvasCTAButtonCollection'
  && content.isFullWidth
  && {
    '<md:pl-4': isItemTextLink('sm', 0),
    '~md:pl-4': isItemTextLink('md', 0),
    'lg:pl-4': isItemTextLink('lg', 0),
    '<md:pr-4': isItemTextLink('sm', -1),
    '~md:pr-4': isItemTextLink('md', -1),
    'lg:pr-4': isItemTextLink('lg', -1)
  }

/**
 * Get style classes for carousel containers
 */
const classContainer = [
  // Mobile slides centring
  'scroll-pa-$p',
  'md:scroll-pa-$p-md',
  // Create a negative margin for the container to compensate slides paddings
  sm?.slidePadding ? '<md:-m-$m' : '',
  md?.slidePadding ? '~md:-m-$m-md' : '',
  lg?.slidePadding ? 'lg:-m-$m-lg' : '',
  // Carousel vs Grid Layout
  {
    '<md:wrap': sm?.type === 'grid',
    '~md:wrap': md?.type === 'grid',
    'lg:wrap': lg?.type === 'grid'
  },
  {
    'items-center': content.type === 'VfCanvasCTAButtonCollection',
    'gap-y-4 md:gap-y-6': content.type === 'VfCanvasCategoryCardCollection'
      && content.variant.includes('elevated-grid'),
    'justify-center': content.type === 'VfCanvasCategoryCardCollection'
      && content.variant.includes('elevated-grid') && items.value.length <= 2,
    'between': content.type === 'VfCanvasLinkedCardCollection'
      && content.variant === 'threeCardGrid_StackedOnSmall'
  },
]

const carouselSettings = computed(() => {
  const {
    autoplay,
    autorotateTime = 0,
    loop,
    showArrows,
    showIndicators,
    showProgress,
    type
  } = content.breakpoints[$viewport.breakpoint] || {}

  return {
    autoplay,
    dragFree: content.type === 'VfCanvasCTAButtonCollection',
    highlightCenter: content.variant === 'highlightCenter',
    interval: autorotateTime * 1000,
    loop,
    sizeControls,
    showArrows,
    showArrowsOnHoverOnly,
    showArrowsOnHoverThreshold,
    showPagination: showIndicators,
    showProgress,
    active: type === 'carousel'
  }
})

const titleTag = titleHeadingTag || (firstElement ? 'h1' : 'h2')

const isSimplifiedTitle = content.type === 'VfCanvasCTAButtonCollection' && content.variant === 'flex'
</script>
